/* InternationalCollabBlock — shared olive hero / section used by both the
 * Landing page and the /international page.
 *
 * Props:
 *   ctaHref: destination of the "Start an International Brief" CTA.
 *            On the landing page this routes to `/international#brief` so a
 *            tap jumps straight to the enquiry form; on /international we
 *            pass `#brief` so the same tap scrolls within the current page.
 *   as:     tag for the section wrapper (defaults to "section").
 *   id:     DOM id for the wrapper (defaults to "international").
 *
 * 2026-10-08 — all copy moved to the i18n dictionary (intl.*) so switching
 * language toggles the Chinese version in one place.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "@/lib/i18n";

const WHATSAPP_NUMBER = "60183628663"; // international, no +
export const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hi Kaily, I'd like to discuss an international F&B / hospitality production brief."
)}`;

export default function InternationalCollabBlock({ ctaHref = "/international#brief", id = "international" }) {
    const { t } = useT();
    // CTA is a Link when the destination contains a path, an anchor-only
    // href renders as a plain anchor so clicks scroll inside the current page.
    const isAnchorOnly = typeof ctaHref === "string" && ctaHref.startsWith("#");
    const ctaProps = {
        "data-testid": "intl-collab-cta",
        className: "inline-flex items-center h-12 px-6 rounded-full bg-cafe-cream text-cafe-espresso text-[13.5px] font-medium hover:-translate-y-0.5 transition-transform",
    };
    const Cta = isAnchorOnly
        ? <a href={ctaHref} {...ctaProps}>{t("intl.cta")}<ArrowUpRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} /></a>
        : <Link to={ctaHref} {...ctaProps}>{t("intl.cta")}<ArrowUpRight className="ml-1.5 h-4 w-4" strokeWidth={1.5} /></Link>;

    return (
        <section id={id} className="py-24 md:py-32 bg-cafe-olive text-cafe-cream" data-testid="international-collab-section">
            <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                <div className="lg:col-span-7">
                    <p className="text-[10.5px] md:text-[11px] uppercase tracking-[0.32em] font-semibold text-cafe-cream/80 mb-3">
                        {t("intl.overline")}
                    </p>
                    <h2 className="font-serif text-4xl md:text-6xl lg:text-[72px] text-cafe-cream leading-[0.95] text-balance" style={{ fontWeight: 500 }}>
                        {t("intl.title_1")}{" "}
                        <em className="font-display italic">{t("intl.title_2")}</em>.
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-cafe-cream/85 leading-relaxed max-w-2xl">
                        {t("intl.body")}
                    </p>
                    <ul className="mt-6 space-y-2 text-cafe-cream/85 leading-relaxed">
                        <li>— {t("intl.bullet_1")}</li>
                        <li>— {t("intl.bullet_2")}</li>
                        <li>— {t("intl.bullet_3")}</li>
                    </ul>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {Cta}
                        <a
                            href={WHATSAPP_LINK}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="intl-collab-wa"
                            className="inline-flex items-center h-12 px-6 rounded-full border border-cafe-cream text-cafe-cream text-[13.5px] font-medium hover:bg-cafe-cream hover:text-cafe-olive transition-colors"
                        >
                            WhatsApp
                        </a>
                    </div>
                </div>
                <div className="lg:col-span-5">
                    <div className="rounded-[2rem] bg-cafe-cream text-cafe-espresso p-7 md:p-10">
                        <p className="text-[10.5px] uppercase tracking-[0.28em] text-cafe-olive font-semibold mb-5">{t("intl.card_overline")}</p>
                        <ul className="space-y-4 text-sm leading-relaxed">
                            <li><strong className="text-cafe-espresso">{t("intl.card.based.k")}:</strong> <span className="text-cafe-espresso/70">{t("intl.card.based.v")}</span></li>
                            <li><strong className="text-cafe-espresso">{t("intl.card.lang.k")}:</strong> <span className="text-cafe-espresso/70">{t("intl.card.lang.v")}</span></li>
                            <li><strong className="text-cafe-espresso">{t("intl.card.invoice.k")}:</strong> <span className="text-cafe-espresso/70">{t("intl.card.invoice.v")}</span></li>
                            <li><strong className="text-cafe-espresso">{t("intl.card.rights.k")}:</strong> <span className="text-cafe-espresso/70">{t("intl.card.rights.v")}</span></li>
                            <li><strong className="text-cafe-espresso">{t("intl.card.deposit.k")}:</strong> <span className="text-cafe-espresso/70">{t("intl.card.deposit.v")}</span></li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}
